<template>
  <CheckboxRoot
    v-bind="forwarded"
    :class="['form-check-root', props.class]"
  >
    <CheckboxIndicator class="form-check-indicator">
      <i icon-check class="bx bx-check" />
      <i icon-indeterminate class="bx bx-minus" />
    </CheckboxIndicator>
  </CheckboxRoot>
</template>

<script setup>
import { computed } from 'vue'
import {
  CheckboxIndicator,
  CheckboxRoot,
  useForwardPropsEmits,
} from 'radix-vue'

/* eslint-disable vue/require-default-prop */
const props = defineProps({
  defaultChecked: { type: Boolean, required: false },
  checked: { type: [Boolean, String], required: false },
  disabled: { type: Boolean, required: false },
  required: { type: Boolean, required: false },
  name: { type: String, required: false },
  value: { type: String, required: false },
  id: { type: String, required: false },
  asChild: { type: Boolean, required: false },
  as: { type: null, required: false },
  class: { type: null, required: false },
})
const emits = defineEmits(['update:checked'])

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<style lang="scss">
.form-check {
  .form-check-root {
    float: left;
    margin-left: -1.5rem;
  }

  .form-label {
    margin-bottom: 0;
  }
}

.form-check-reverse {
  .form-check-root {
    float: right;
    margin-right: -1.5rem;
    margin-left: 0;
  }
}

.form-check-root {
  --gu-form-check-bg: var(--gu-body-bg);
  --gu-form-check-text-color: var(--gu-white);
  --gu-form-check-size: 1rem;

  background-color: var(--gu-form-check-bg);
  border-radius: var(--gu-border-radius-sm);
  border: var(--gu-border-width) solid var(--gu-border-color);
  color: var(--gu-form-check-text-color);
  cursor: default;
  font-size: 0.875rem;
  height: var(--gu-form-check-size);
  margin-top: 0.25rem;
  padding: 0;
  vertical-align: top;
  width: var(--gu-form-check-size);

  &:focus {
    border-color: rgba(var(--gu-primary), 0.5);
    box-shadow: 0 0 0 .25rem rgba(var(--gu-primary-rgb), 0.25);
    outline: 0;
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &:disabled, &[disabled] {
    ~ .form-label {
      cursor: default;
      opacity: 0.5;
    }
  }

  .bx {
    display: none;
    font-weight: bold;
  }

  &[data-state="checked"], &[data-state="indeterminate"] {
    background-color: var(--gu-primary);
    border-color: var(--gu-primary);
  }

  &[data-state="checked"] {
    [icon-check] {
      display: block;
    }
  }

  &[data-state="indeterminate"] {
    [icon-indeterminate] {
      display: block;
    }
  }

  .form-check & {
    padding: 0;
  }
}

.form-check-indicator {
  color: currentColor;
}
</style>
